import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { AggregationMethod } from '../../results-helper/grouped-result-table/grouped-result-table.component';
import { Results } from '../../../models/results/results.model';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import {
  ResultSetConfig,
  ResultSetConfigInterface,
  ResultSetID,
  ResultSetIds
} from '../../../models/results/result-set-config.model';
import { ResultSetConfigService } from '../../results-helper/result-set-config.service';
import Utils from '../../../util';
import {
  ResultSetDropdownId
} from '../../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';
import { ISPL } from '../../../models/results/financial/ispl/ispl.model';

@Component({
  selector: 'app-baseline-income-statement',
  templateUrl: './baseline-income-statement.component.html',
  styleUrls: ['./baseline-income-statement.component.scss']
})
export class BaselineIncomeStatementComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {

  private onDestroyNotifier = new EventEmitter();

  categories: DropdownDisplayItem[] = [];

  selectedCategory?: DropdownDisplayItem;
  selectedCurrency?: Unit | null;

  currencyUnits: Unit[] = [];
  currencyUnitsLoaded = false;

  tableData = [];

  aggMethod = AggregationMethod.SUM;

  exchangeRateDate?: string;
  baselineResults?: Results;

  trigger?: string;

  isplResults: ISPL | null = null;

  unitServiceReady = false;
  resultsLoaded = false;
  projectCommServiceReady = false;

  PROJECT_ID = '';
  resultConf?: ResultSetConfig;

  constructor(
    private projectCommService: ProjectCommunicationService,
    private toastrService: ConfiguredToastrService,
    private unitService: UnitService,
    private translationService: AnessaTranslationService,
    private resultSetConfigService: ResultSetConfigService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(reloaded => {
        this.resultsLoaded = reloaded;
        if (reloaded) {
          this.setResults();
        }
      });
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded: boolean) => {
        this.projectCommServiceReady = loaded;
        if (loaded) {
          this.PROJECT_ID = this.projectCommService.getProjectId() ?? '';
          this.setup();
        }
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitServiceReady = loaded;
        this.setResults();
      })
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        const previousSelection = this.selectedCategory;
        this.setTranslations();
        this.restoreCategorySelection(previousSelection);
        this.trigger = language;
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setup() {
    if (this.servicesReady()) {
      // default selection
      if (!this.selectedCategory) {
        this.selectedCategory = this.categories.find(cat => cat.value === 'detailed income statement');
      }
      // process result conf (sets table data as well)
      this.fetchResultSetConfigData();
    }
  }

  servicesReady() {
    return this.resultsLoaded && this.unitServiceReady &&
      this.currencyUnitsLoaded && this.projectCommServiceReady;
  }

  setTranslations() {
    this.categories = [
      new DropdownDisplayItem('RESULTS.DATA.REVENUES', 'RESULTS.DATA.ISPL.REVENUES'),
      new DropdownDisplayItem('RESULTS.DATA.EXPENSES', 'RESULTS.DATA.ISPL.EXPENSES'),
      new DropdownDisplayItem('RESULTS.DATA.EARNINGS', 'RESULTS.DATA.ISPL.EARNINGS'),
      new DropdownDisplayItem('RESULTS.DATA.DETAILED_INCOME_STATEMENT', 'detailed income statement')
    ];
  }

  getFinExchange(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyUnits = units;

        // sort rates alphabetically
        this.currencyUnits.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        this.currencyUnitsLoaded = true;

        // Select default unit if set or CAD otherwise
        if (this.projectCommService.getProjectDefaults().currency != null) {
          this.selectedCurrency = this.unitService.getUnitById(this.projectCommService.getProjectDefaults().currency);
        } else {
          this.selectedCurrency = this.currencyUnits.find(currency => currency.displayName === 'CAD');
        }
        this.setup();
      }
    );
  }

  setResults() {
    if (!this.resultsLoaded || !this.unitServiceReady) return;
    const results = this.projectCommService.getResults();
    if (results && results.financial) {
      this.baselineResults = results;
      this.getFinExchange(this.baselineResults.created);
    } else {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS')
      );
    }
  }

  /**
   * Sets the data to be displayed
   */
  setTableData(): void {
    this.tableData = [];
    if (!this.baselineResults) return;
    let plStatement = this.baselineResults.financial.ispl;
    plStatement = Utils.deepCopy(plStatement);
    this.removeCategories(plStatement);
    this.convertValuesCurrency(plStatement);
    this.isplResults = plStatement;
  }

  removeCategories(plStatement: ISPL) {
    if (this.selectedCategory && this.selectedCategory.value !== 'detailed income statement') {
      const isplKeys = Object.keys(plStatement) as (keyof ISPL)[];
      for (const key of isplKeys) {
        if (this.selectedCategory.value !== key) {
          delete plStatement[key];
        }
      }
    }
  }

  convertValuesCurrency(result: LooseObject) {
    const leafKeys = ['RESULTS.DATA.UNIT', 'RESULTS.DATA.VALUES'];
    if (leafKeys.some(leafKey => Object.keys(result).includes(leafKey))) { // results object is a leaf
      const values = result.values;
      const currency = result.unit;
      if (!values || !currency) return;
      for (let i = 0; i < values.length; i++) {
        values[i] = Unit.convertValue(values[i], currency, this.selectedCurrency.id);
      }
    } else {
      for (const subResult of Object.values(result)) {
        this.convertValuesCurrency(subResult);
      }
    }
  }

  /**
   * Checks if at least one result is selected
   */
  isShowResults(): boolean {
    return !!(this.selectedCategory && this.selectedCurrency);
  }

  private restoreCategorySelection(previousSelection?: DropdownDisplayItem) {
    this.selectedCategory = this.categories.find(cat => {
      return cat.value === previousSelection?.value
    });
  }

  private fetchResultSetConfigData() {
    if (this.projectCommServiceReady && this.resultsLoaded) {
      this.resultSetConfigService.fetch(
        [ResultSetID.INCOME_STATEMENT],
        this.PROJECT_ID
      )
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(res => {
          this.setupResultSetConfigs(res);
          this.setTableData();
        });
    }
  }

  private setupResultSetConfigs(res: ResultSetIds): void {
    if (!res && !res['obj']) {
      throw Error('invalid response from result set config, unable to process');
    }
    const processedObj = Utils.restoreMapsInJson(res['obj'], 'dropdownDefaults');
    const resultSetConfInterface: ResultSetConfigInterface = processedObj[ResultSetID.INCOME_STATEMENT];
    if (resultSetConfInterface) {
      this.resultConf = new ResultSetConfig(resultSetConfInterface);
    } else {
      this.resultConf = new ResultSetConfig();
      this.resultConf.resultSetID = ResultSetID.INCOME_STATEMENT;
    }
    this.processResultSetDefaults();
  }

  processResultSetDefaults(): void {
    if (!this.projectCommServiceReady || !this.resultsLoaded) return;
    if (!this.resultConf || !this.resultConf.dropdownConfigEnabled) return;
    const defaults = this.resultConf.dropdownDefaults;
    if (defaults.has(ResultSetDropdownId.INCOME_STATEMENT_CAT)) {
      this.selectedCategory = this.categories.find(
        cat => cat.value === defaults.get(ResultSetDropdownId.INCOME_STATEMENT_CAT)
      );
    }
    if (defaults.has(ResultSetDropdownId.INCOME_STATEMENT_CURR)) {
      this.selectedCurrency = this.currencyUnits.find(
        cur => cur.id === defaults.get(ResultSetDropdownId.INCOME_STATEMENT_CURR)
      );
    }
  }

  onResultSetDefaultChange() {
    if (this.resultConf?.dropdownConfigEnabled && Utils.isMap(this.resultConf.dropdownDefaults)) {
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.INCOME_STATEMENT_CAT, this.selectedCategory?.value);
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.INCOME_STATEMENT_CURR, this.selectedCurrency?.id);
      this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
    }
  }

  setResultSetConfDisabled() {
    if (this.resultConf?.dropdownConfigEnabled && this.PROJECT_ID) {
      this.resultConf.dropdownConfigEnabled = false;
      this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
    }
  }
}
